import { Avatar, ConfigProvider, DatePicker, Popover } from "antd"
import styles from "./ElectricPriceToday.module.css"
import React, { useEffect, useState } from "react"
import { Line } from "react-chartjs-2"
import annotationPlugin from "chartjs-plugin-annotation"
import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react"
import SwiperSection from "../../_HomeComponents/SwiperSection/SwiperSection"
import FaqSection from "../../_Landings/B2C/FaqSection/FaqSection"
import { B2cFaq } from "../../../constants/questions.constants"
import RichText from "./RichText"
import locale from "antd/locale/es_ES"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js"
import { useRouter } from "next/router"
import { formatDate, FormatSlugDate } from "../../../utils/dateUtils"
import dayjs from "../../../dayjsConfig"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
)

interface Price {
  value: number
  percentage: number
  datetime: string
  time: string
}

interface PriceCardProps {
  prices: {
    mañana: Price[]
    tarde: Price[]
    noche: Price[]
  }
  maxPrice: number
}

const SmCard: React.FC<{
  children: React.ReactNode
  icon: React.ReactNode
  color: string
}> = ({ children, icon, color }) => (
  <div className={styles.Smcard}>
    <Avatar
      style={{
        backgroundColor: color,
        color: "#FFFFFF",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        width: "50px",
        height: "50px",
      }}
      icon={icon}
    />
    {children}
  </div>
)

const AveragePriceCards = ({ avgPrice, minPrice, maxPrice }: any) => (
  <div className={styles.averagePriceCards}>
    <SmCard icon={<ArrowRight />} color="var(--Aguamarina-Fiva)">
      <div style={{ textAlign: "center" }}>
        Precio medio del día
        <br />
        <p
          style={{
            fontSize: "22px",
            display: "block",
            fontWeight: 700,
          }}
        >
          {avgPrice}{" "}
          <span style={{ fontSize: "16px", fontWeight: 400 }}>€/kWh</span>
        </p>
      </div>
    </SmCard>
    <SmCard icon={<ArrowDown />} color="green">
      <div style={{ textAlign: "center" }}>
        Precio más barato
        <p
          style={{
            fontSize: "22px",
            display: "block",
            fontWeight: 700,
          }}
        >
          {minPrice.toFixed(4)}{" "}
          <span style={{ fontSize: "16px", fontWeight: 400 }}>€/kWh</span>
        </p>
      </div>
    </SmCard>
    <SmCard icon={<ArrowUp />} color="red">
      <div style={{ textAlign: "center" }}>
        Precio más caro
        <p
          style={{
            fontSize: "22px",
            display: "block",
            fontWeight: 700,
          }}
        >
          {maxPrice.toFixed(4)}{" "}
          <span style={{ fontSize: "16px", fontWeight: 400 }}>€/kWh</span>
        </p>
      </div>
    </SmCard>
  </div>
)

const getColor = (value: number, maxPrice: number): string => {
  if (value <= 0.6 * maxPrice) return "green"
  if (value <= 0.8 * maxPrice) return "orange"
  return "red"
}

const ResponsiveList = () => (
  <ul className={styles.responsiveList}>
    <li className={styles.listItem}>
      <div className={`${styles.circle} ${styles.green}`}></div>
      <span>Precios bajos: Entre un 40 a 60% del precio más alto del día</span>
    </li>
    <li className={styles.listItem}>
      <div className={`${styles.circle} ${styles.orange}`}></div>
      <span>Precios medios: Entre un 60 a 80% del precio más alto del día</span>
    </li>
    <li className={styles.listItem}>
      <div className={`${styles.circle} ${styles.red}`}></div>
      <span>Precios altos: Entre un 80 a 100% del precio más alto del día</span>
    </li>
  </ul>
)

const PriceCard: React.FC<PriceCardProps> = ({ prices, maxPrice }) => (
  <div className={styles.card}>
    <div className={styles.inercard}>
      {["Mañana", "Tarde", "Noche"].map((period) => (
        <ul key={period}>
          <h4>{period}</h4>
          {prices[period.toLowerCase() as keyof typeof prices].map(
            ({ time, value }) => (
              <li
                key={time}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "5px",
                }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: getColor(value, maxPrice),
                    marginRight: "10px",
                  }}
                ></div>
                <div
                  style={{
                    display: "flex",
                    gap: "22px",
                  }}
                >
                  <span style={{ flexGrow: 1 }}>{time}h</span>
                  <span
                    style={{
                      fontWeight: 700,
                    }}
                  >
                    {value.toFixed(3)} €/kWh
                  </span>
                </div>
              </li>
            )
          )}
        </ul>
      ))}
    </div>
    <ResponsiveList />
  </div>
)

const PriceGraph: React.FC<{ prices: Price[] }> = ({ prices }) => {
  const data = {
    labels: prices.map((price) => price.time.slice(0, 2) + "h"),
    datasets: [
      {
        label: "Precio €/kWh",
        data: prices.map((price) => price.value),
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.4, // Makes the line more curved
        fill: true,
      },
    ],
  }

  const avgPrice =
    prices.reduce((acc, price) => acc + price.value, 0) / prices.length

  const options: any = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Precios por horas a lo largo del día",
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(
              2
            )} €/kWh`
          },
        },
      },
      annotation: {
        annotations: {
          line1: {
            type: "line",
            yMin: avgPrice,
            yMax: avgPrice,
            borderColor: "black",
            borderWidth: 2,
            label: {
              content: `Precio medio: ${avgPrice.toFixed(2)} €/kWh`,
              enabled: true,
              position: "center",
            },
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false, // Hide X axis grid lines
        },
      },
      y: {
        display: true, // Hide Y axis
      },
    },
    elements: {
      point: {
        radius: 0, // Hide data points
      },
      line: {
        borderWidth: 2, // Line width
      },
    },
  }

  return (
    <div className={styles.graphCard}>
      <h4>Evolución de los precios a lo largo del día</h4>
      <Line data={data} options={options} />
    </div>
  )
}

interface ElectricPriceTodayProps {
  price: any
  date?: string
}

const ElectricPriceToday: React.FC<ElectricPriceTodayProps> = ({
  price,
  date,
}) => {
  const [visible, setVisible] = useState(false)
  const router = useRouter()

  if (!price || !price?.length) {
    return <div>No hay datos de precio disponibles</div>
  }

  const pvpc: any = price[0].attributes.values

  // Calcular el precio máximo, mínimo y la media
  const maxPrice = Math.max(...pvpc.map((item: any) => item.value))
  const minPrice = Math.min(...pvpc.map((item: any) => item.value))
  const avgPrice = (
    pvpc.reduce((acc: any, item: any) => acc + item.value, 0) / pvpc.length
  ).toFixed(4)

  // Encontrar las horas correspondientes al máximo y mínimo
  const maxPriceItem = pvpc.find((item: any) => item.value === maxPrice)!
  const minPriceItem = pvpc.find((item: any) => item.value === minPrice)!

  // Agrupar precios por periodos del día (mañana, tarde, noche)
  const periods = {
    mañana: [] as Price[],
    tarde: [] as Price[],
    noche: [] as Price[],
  }

  pvpc?.forEach((item: any) => {
    const hour = parseInt(item.time.split(":")[0], 10)
    if (hour <= 13 && hour >= 6) periods.mañana.push(item)
    else if (hour >= 14 && hour <= 21) periods.tarde.push(item)
    else periods.noche.push(item)
  })

  // Función para formatear la fecha en "DD de MMMM, YYYY"
  const formatDisplayDate = (dateString: string) => {
    const [day, month, year] = dateString.split("-")
    const months: any = {
      enero: "enero",
      febrero: "febrero",
      marzo: "marzo",
      abril: "abril",
      mayo: "mayo",
      junio: "junio",
      julio: "julio",
      agosto: "agosto",
      septiembre: "septiembre",
      octubre: "octubre",
      noviembre: "noviembre",
      diciembre: "diciembre",
    }
    return `${parseInt(day)} de ${months[month.toLowerCase()]}, ${year}`
  }

  const handleDateChange = (date: any, dateString: string) => {
    const todayDate = new Date().setHours(0, 0, 0, 0) // Set today's date to midnight for comparison
    const selectedDate = date.toDate().setHours(0, 0, 0, 0)

    if (selectedDate === todayDate) {
      router.push("/precio-luz-hoy")
    } else if (date) {
      const formattedDate = FormatSlugDate(date.toDate())
      router.push(`/precio-luz/${formattedDate}`)
    }
  }
  const today = date ? formatDisplayDate(date) : formatDate(new Date())

  const disabledDate = (current: any) => {
    const startDate = new Date("2021-06-01")
    const endDate = new Date()
    return current && (current < startDate || current > endDate)
  }

  return (
    <div>
      <div className={styles.flexColumn}>
        <div className={styles.maxWidth}>
          <Popover
            content={
              <span>
                {date
                  ? `El precio medio de la luz el dia ${today} fue`
                  : "El precio medio de la luz hoy es"}
                <br /> <strong style={{ fontSize: "18px" }}>{avgPrice}</strong>{" "}
                €/kWh
              </span>
            }
            title="Mensaje de Lucy"
            trigger="click"
            open={visible}
            onOpenChange={(visible) => setVisible(visible)}
            overlayClassName="custom-popover-content"
          >
            <img
              src="/icons/lucy.svg"
              alt="Lucy"
              className={styles["pulse"]}
              width={90}
              height={90}
            />
          </Popover>
          <div>
            <h1 className={styles.title}>
              {date
                ? `Precio de la luz el dia ${today}: histórico de precios`
                : "Precio de la luz hoy: horas más baratas"}
            </h1>
            <h2 className={styles.subtitle}>
              Medio: {avgPrice} €/kWh | Más barato: {minPrice.toFixed(4)} €/kWh
              a las {minPriceItem.time}h | Más caro: {maxPrice.toFixed(4)} €/kWh
              a las {maxPriceItem.time}h
            </h2>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              marginTop: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Avatar
                size="large"
                icon={
                  <img
                    src={
                      "https://res.cloudinary.com/dovplwabx/image/upload/c_fill,w_500,h_500/v1716737990/nbujq55xmvldhk68dbp3.jpg"
                    }
                    alt="icon logo"
                  />
                }
              />

              <div>
                <span style={{ color: "grey", fontSize: "16px" }}>
                  Pablo Andreu
                </span>
                <br />
                <span
                  style={{
                    color: "var(--fiva-color)",
                    fontSize: "16px",
                  }}
                >
                  Fiva Energía.
                </span>
              </div>
            </div>
          </div>
          <br />
          <h3 className={styles.text}>
            Precio de la luz por horas
            <br />
            {today}
          </h3>
          <br />
          <AveragePriceCards
            avgPrice={avgPrice}
            minPrice={minPrice}
            maxPrice={maxPrice}
          />
          <br />
          <PriceCard prices={periods} maxPrice={maxPrice} />
          <span style={{ fontSize: "14px" }}>
            Fuente: Red Eléctrica de España
            <br />
            Precio medio del día: Media aritmética de los precios del día en
            función del precio de tarifa. Precio en kilovatios hora.
          </span>
          <br />
          <div className={styles.calendar}>
            <h3 className={styles.text}>
              ¿Quieres saber el precio de la luz de otro día?
            </h3>
            <label htmlFor="date-picker">Selecciona una fecha</label>
            <ConfigProvider locale={locale}>
              <DatePicker
                id="date-picker"
                onChange={handleDateChange}
                format="DD MMMM YYYY"
                placeholder="DD/MM/YYYY"
                className={styles.datePicker}
                disabledDate={disabledDate}
              />
            </ConfigProvider>
          </div>
        </div>
      </div>
      <SwiperSection />
      <div className={styles.flexColumn}>
        <div className={styles.maxWidth}>
          <PriceGraph prices={pvpc} />
          <p style={{ fontSize: "16px" }}>
            👉 Precios para la nueva tarifa de luz 2.0, extraídos de Red
            Eléctrica de España sin impuestos añadidos. Actualizados a {today}.
          </p>
          <br />
          <RichText />
        </div>
      </div>
      <FaqSection questions={B2cFaq} />
    </div>
  )
}

export default ElectricPriceToday
